import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import Spinner from '../../components/UI/Spinner/Spinner';
import {
  fetchSalesOrders,
  selectSalesOrdersStatus,
  selectSalesOrders,
  SalesOrderInfo,
  selectServicesTranslations,
  fetchServicesTranslations,
} from '../../store/slices/salesOrders';
import { useAppDispatch, useAppSelector } from '../../store/store';
import classes from './Consultations.module.css';
import DocumentsTab from './Tabs/DocumentsTab';
import FindingsTab from './Tabs/FindingsTab';
import { ReportTable } from '../../components/UI/Layouts/ReportTable';
import { ReportsPageHeader } from '../../components/Headers/ReportsPageHeader';
import { ReportsTableHeader } from '../../components/Headers/ReportsTableHeader';
import infoIcon from '../../assets/icons/info.svg';
import sunIcon from '../../assets/icons/sun.svg';
import thumbsUpIcon from '../../assets/icons/thumbsup.svg';
import documentsIcon from '../../assets/icons/directory.svg';
import { CustomerProfile } from './Tabs/CostumerProfile';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { statusNameById } from '../../utils/statusUtils';
import { IVideoConsultingReportsExport } from '../../components/UI/ExportToExcel/ExportToExcel';
import {
  IAttachment,
  IDigitalHealthProfile,
} from '../../components/UI/SelectedSalesOrder/DigitalHealthProfileTab';
import { toast } from 'react-toastify';
import {
  IAccountInfo,
  IPerson,
  selectAccountInfo,
  selectLocationId,
  selectPersonLoggedIn,
  selectUserRole,
} from '../../store/slices/auth';
import { IDoctor, fetchDoctors, selectDoctors } from '../../store/slices/doctors';
import { DOCTOR_USER_ROLE, NURSE_USER_ROLE } from '../../constants/userRolesConstants';
import { findProductWithTaIdAndTransactionId } from '../../helpers';
import { resetVitalSigns } from '../../store/slices/vitalSigns';
import { ConsultationTypes } from '../../enums/enums';
import CustomPagination from '../../components/UI/CustomPagination/CustomPagination';
import { useTranslation } from '../../hooks/useTranslations';

type Props = {
  hideHeader?: boolean;
};

const Consultations: React.FC<Props> = ({ hideHeader }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { translations } = useTranslation();
  const { sales_order_id, sales_order_item_id } = useParams<{
    sales_order_id?: string;
    sales_order_item_id?: string;
  }>();
  const getSalesOrdersStatus = useAppSelector<string>(selectSalesOrdersStatus);
  const salesOrders = useAppSelector<SalesOrderInfo[]>(selectSalesOrders);
  const [selectedSalesOrder, setSelectedSalesOrder] = useState<SalesOrderInfo>();
  const [isEditingCustomer, setIsEditingCustomer] = useState<boolean>(false);
  const [firstNameSearch, setFirstNameSearch] = useState<string>('');
  const [statusSearch] = useState<string>('');
  const [bookingDateSearch] = useState<string>('');
  const [startDateValue, setStartDateValue] = useState<Date | undefined>();
  const [endDateValue, setEndDateValue] = useState<Date | undefined>();
  const [selectedTab, setSelectedTab] = useState(0);
  const [productOrder, setProductOrder] = useState<IDigitalHealthProfile>();
  const [filterByStatus, setFilterByStatus] = useState<number>();
  const [filterByService, setFilterByService] = useState<number>();

  const accountInfo = useAppSelector<IAccountInfo>(selectAccountInfo);
  const locationId = useAppSelector<IAccountInfo>(selectLocationId);
  const doctors: IDoctor[] = useAppSelector<IDoctor[]>(selectDoctors);
  const personLoggedIn = useAppSelector<IPerson | undefined>(selectPersonLoggedIn);
  const userRole: string = useAppSelector<string>(selectUserRole);
  const lang = localStorage.getItem('lang') || 'en';

  const servicesTranslations = useAppSelector(selectServicesTranslations);

  useEffect(() => { dispatch(fetchServicesTranslations()); }, [dispatch]);

  const getTranslatedServiceName = (
    serviceId: string | number,
    lang: string
  ): string => {
    const serviceTranslation = servicesTranslations.find(
      (st: any) => st.service_id === serviceId.toString()
    );
    if (!serviceTranslation) return '';

    const translation = serviceTranslation.translations.find(
      (t: any) => t.language.trim() === lang
    );

    return translation?.service_name || '';
  };


  const Tabs: { displayName: string; value: number; icon: any }[] = [
    {
      displayName: 'patient.info',
      value: 0,
      icon: infoIcon,
    },
    {
      displayName: 'findings',
      value: 1,
      icon: sunIcon,
    },
    {
      displayName: 'recommendations',
      value: 2,
      icon: thumbsUpIcon,
    },
    {
      displayName: 'documents',
      value: 3,
      icon: documentsIcon,
    },
  ];

  const consultationSalesOrders = useMemo(() => {
    let tempoSalesOrders = salesOrders.filter(
      (salesOrder) =>
        salesOrder.service_service_group.service_group_id ===
        ConsultationTypes.VIDEO_CONSULTATION ||
        salesOrder.service_service_group.service_group_id ===
        ConsultationTypes.ON_SITE_VIDEO_CONSULTATION ||
        salesOrder.service_service_group.service_group_id ===
        ConsultationTypes.FACE_TO_FACE_CONSULTATION ||
        salesOrder.service_service_group.service_group_id === ConsultationTypes.REVIEW_CONSULTATION
    );

    return tempoSalesOrders;
  }, [salesOrders]);

  const consultationSalesOrdersToDisplay = useMemo(() => {
    let tempoSalesOrders = [...consultationSalesOrders];
    if (firstNameSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        `${salesOrder.customer_person.person_firstname}${salesOrder.customer_person.person_lastname}`
          .toLowerCase()
          .includes(firstNameSearch.toLocaleLowerCase())
      );
    // if (lastNameSearch.trim().length > 0)
    //   tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
    //     salesOrder.customer_person.person_lastname
    //       .toLowerCase()
    //       .includes(lastNameSearch.toLocaleLowerCase())
    //   );
    if (statusSearch.trim().length > 0)
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) => {
        const tempoStatus =
          salesOrder.work_order?.work_order_status_id === 2
            ? translations('closed')
            : salesOrder.work_order?.work_order_status_id === 1
              ? translations('pending')
              : salesOrder.work_order?.work_order_status_id === 4
                ? translations('parked')
                : translations('open');
        if (tempoStatus.toLowerCase().includes(statusSearch.toLocaleLowerCase())) return true;
        return false;
      });
    if (bookingDateSearch.trim().length > 0) {
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) =>
        format(
          parseISO(salesOrder?.sales_order?.datetime_create?.toString() ?? '01-01-1111'),
          'dd.MM.yyyy'
        )
          .toLowerCase()
          .includes(bookingDateSearch.toLocaleLowerCase())
      );
    }

    if (filterByService) {
      tempoSalesOrders = tempoSalesOrders.filter(
        (salesOrder) => salesOrder.service.service_id === filterByService
      );
    }
    if (filterByStatus) {
      tempoSalesOrders = tempoSalesOrders.filter((salesOrder) => {
        const status_id = filterByStatus === -1 ? undefined : filterByStatus;
        return salesOrder.work_order?.work_order_status_id === status_id;
      });
    }
    // FILTER MISSED APPOINTMENTS
    tempoSalesOrders = tempoSalesOrders.filter(
      (el) => el.sales_order_appointment?.sales_order_appointment_status !== 'M'
    );
    tempoSalesOrders = tempoSalesOrders.map((sOrder) => ({
      ...sOrder,
      translated_service_name: getTranslatedServiceName(sOrder.service.service_id, lang),
    }));
    return [...tempoSalesOrders];
  }, [
    firstNameSearch,
    statusSearch,
    bookingDateSearch,
    consultationSalesOrders,
    startDateValue,
    endDateValue,
    filterByStatus,
    filterByService,
    lang,
    servicesTranslations,
  ]);

  const canEditConsultation = useMemo(() => {
    if (userRole === 'admin') {
      return true;
    }
    const doctorLoggedIn = doctors.find(
      (doctor) => parseInt(doctor.person_id) == Number(personLoggedIn?.person_id)
    );
    if (userRole === DOCTOR_USER_ROLE && doctorLoggedIn) {
      if (
        doctorLoggedIn.person_appointments.find(
          (appointment) =>
            appointment.sales_order_id == selectedSalesOrder?.sales_order.sales_order_id &&
            appointment.sales_order_item_id ==
            selectedSalesOrder?.sales_order_item.sales_order_item_id
        )
      ) {
        return true;
      }
    }

    return false;
  }, [selectedSalesOrder, doctors, personLoggedIn, userRole]);

  useEffect(() => {
    dispatch(resetVitalSigns());
    dispatch(fetchSalesOrders());
  }, [dispatch]);

  useEffect(() => {
    if (locationId) {
      dispatch(fetchDoctors());
    }
  }, [dispatch, locationId]);

  useEffect(() => {
    if (sales_order_id && sales_order_item_id && salesOrders.length > 0) {
      const salesOrder = salesOrders.find(
        (salesOrder) =>
          salesOrder.sales_order.sales_order_id === parseInt(sales_order_id) &&
          salesOrder.sales_order_item.sales_order_item_id === parseInt(sales_order_item_id)
      );
      if (salesOrder) {
        setSelectedSalesOrder(salesOrder);
        setIsEditingCustomer(true);
      } else {
        navigate('/web/consultations/reports', { replace: true });
      }
    }
  }, [sales_order_id, sales_order_item_id, salesOrders]);

  const checkIfCanEditConsultation = (sOrder: SalesOrderInfo): boolean => {
    if (userRole === 'admin' || userRole === NURSE_USER_ROLE) {
      return true;
    }
    const doctorLoggedIn = doctors.find(
      (doctor) => parseInt(doctor.person_id) == Number(personLoggedIn?.person_id)
    );
    if (userRole === DOCTOR_USER_ROLE && doctorLoggedIn) {
      if (
        doctorLoggedIn.person_appointments.find(
          (appointment) => appointment.work_order_id == sOrder?.work_order?.work_order_id
        )
      ) {
        return true;
      }
    }
    return sOrder?.work_order?.work_order_status_id === 2;
  };

  const handleDownload = async () => {
    const attachmentWithTestResult = productOrder?.attachments?.find(
      (att: IAttachment) => att.attachment_type_id === 1
    );

    if (attachmentWithTestResult && attachmentWithTestResult.link) {
      window.open(
        `${window.location.protocol}//${window.location.host}${attachmentWithTestResult.link}`,
        '_blank'
      );
    } else {
      toast.error(translations('error.no_attachments').toString(), {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  };

  const handleActionClick = (row: any) => {
    const salesOrderRow = row as SalesOrderInfo;
    if (
      salesOrderRow.work_order?.work_order_status_id &&
      checkIfCanEditConsultation(salesOrderRow)
    ) {
      navigate(
        `/web/reports/edit/${salesOrderRow?.sales_order.sales_order_id}/${salesOrderRow?.sales_order_item.sales_order_item_id}`
      );
      return;
    }
    toast.error(translations('no.edit.permissions').toString(), {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
    // setSelectedSalesOrder(salesOrderRow);
    // setIsEditingCustomer(true);
  };

  useEffect(() => {
    const fetchConsultations = async () => {
      const productFound = await findProductWithTaIdAndTransactionId(selectedSalesOrder);
      setProductOrder(productFound);
    };
    if (
      selectedSalesOrder?.work_order?.work_order_status_id === 2 ||
      selectedSalesOrder?.work_order?.work_order_status_id === 3
    ) {
      fetchConsultations();
    }
  }, [selectedSalesOrder, accountInfo]);

  const maxDate = useMemo(() => {
    const dates = consultationSalesOrders
      .filter((salesOrder) => salesOrder.sales_order_appointment.start)
      .map((salesOrder) => new Date(salesOrder.sales_order_appointment.start ?? '').getTime() ?? 0);
    return new Date(Math.max.apply(null, dates));
  }, [consultationSalesOrders]);

  const serviceTypes = useMemo(() => {
    const uniqueServices: { [key: string]: { service_id: number; service_name: string } } = {};

    consultationSalesOrders.forEach((data) => {
      const { service } = data;
      if (service) {
        const { service_id, service_name } = service;
        if (service_id && service_name) {
          uniqueServices[service_id] = { service_id, service_name };
        }
      }
    });
    return Object.values(uniqueServices);
  }, [consultationSalesOrders]);

  const [bookingSortOrder, setBookingSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [appointmentSortOrder, setAppointmentSortOrder] = useState<'asc' | 'desc' | null>(null);

  const handleDateSort = (columnType: 'booking' | 'appointment') => {
    if (columnType === 'appointment') {
      setBookingSortOrder(null);
      if (appointmentSortOrder === null || appointmentSortOrder === 'desc') {
        setAppointmentSortOrder('asc');
        return 'asc';
      } else {
        setAppointmentSortOrder('desc');
        return 'desc';
      }
    } else {
      setAppointmentSortOrder(null);
      if (bookingSortOrder === null || bookingSortOrder === 'desc') {
        setBookingSortOrder('asc');
        return 'asc';
      } else {
        setBookingSortOrder('desc');
        return 'desc';
      }
    }
  };

  const sortByBookingDate = (items: SalesOrderInfo[], order: 'asc' | 'desc') => {
    return [...items].sort((a, b) => {
      const dateA = new Date(a.sales_order.datetime_create).getTime();
      const dateB = new Date(b.sales_order.datetime_create).getTime();
      if (order === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  const sortByAppointmentDate = (items: SalesOrderInfo[], order: 'asc' | 'desc') => {
    return [...items].sort((a, b) => {
      const dateA = a.sales_order_appointment?.start
        ? new Date(a.sales_order_appointment.start).getTime()
        : 0;
      const dateB = b.sales_order_appointment?.start
        ? new Date(b.sales_order_appointment.start).getTime()
        : 0;
      if (order === 'asc') {
        return dateA - dateB;
      } else {
        return dateB - dateA;
      }
    });
  };

  const sortedDisplaySalesOrders = useMemo(() => {
    if (appointmentSortOrder) {
      return sortByAppointmentDate(consultationSalesOrdersToDisplay, appointmentSortOrder);
    }
    if (bookingSortOrder) {
      return sortByBookingDate(consultationSalesOrdersToDisplay, bookingSortOrder);
    }
    return consultationSalesOrdersToDisplay;
  }, [consultationSalesOrdersToDisplay, appointmentSortOrder, bookingSortOrder]);

  return (
    <div className={classes.container}>
      {!hideHeader && (
        <ReportsPageHeader
          label={translations('customer.report').toString()}
          selectedSalesOrder={selectedSalesOrder}
          onBackButtonClick={() => {
            setIsEditingCustomer(false);
            setSelectedSalesOrder(undefined);
          }}
        />
      )}
      <ReportsTableHeader
        tabs={Tabs}
        firstNameSearch={firstNameSearch}
        setFirstNameSearch={setFirstNameSearch}
        dateBegin={startDateValue}
        setDateBegin={setStartDateValue}
        dateEnd={endDateValue}
        setDateEnd={setEndDateValue}
        maxAppointmentDate={maxDate}
        selectedSalesOrder={selectedSalesOrder}
        selectedTab={selectedTab}
        isDropdown
        setSelectedTab={setSelectedTab}
        onClose={() => {
          setIsEditingCustomer(false);
          setSelectedSalesOrder(undefined);
        }}
        showDownloadButton={isEditingCustomer ? true : false}
        handleDownload={handleDownload}
        filterByService={filterByService}
        setFilterByService={setFilterByService}
        serviceTypes={serviceTypes}
        filterByStatus={filterByStatus}
        statusTypes={
          accountInfo.consultation_with_extra_steps
            ? [
              { id: -1, label: translations('open') },
              { id: 1, label: translations('prepare') },
              { id: 2, label: translations('closed') },
              { id: 4, label: translations('parked') },
              { id: 5, label: translations('prepared') },
              { id: 6, label: translations('started') },
            ]
            : [
              { id: -1, label: translations('open') },
              { id: 1, label: translations('pending') },
              { id: 2, label: translations('closed') },
              { id: 4, label: translations('parked') },
            ]
        }
        setFilterByStatus={setFilterByStatus}
        showExportButton={isEditingCustomer ? false : true}
        dataToExport={consultationSalesOrdersToDisplay.map((data: SalesOrderInfo) => {
          return {
            sales_order_id: data?.sales_order?.sales_order_id,
            last_name: data?.customer_person?.person_lastname,
            first_name: data?.customer_person?.person_firstname,
            status: translations(
              statusNameById(
                translations,
                data?.work_order?.work_order_status_id,
                accountInfo.consultation_with_extra_steps,
                true
              )
            ),
            service_name: data.service?.service_name,
            booking_date: moment(data?.sales_order?.datetime_create).format('DD-MM-YYYY HH:mm'),
          } as IVideoConsultingReportsExport;
        })}
      />

      {isEditingCustomer ? (
        <>
          {selectedSalesOrder && selectedTab === 0 && (
            <CustomerProfile selectedSalesOrder={selectedSalesOrder} />
          )}
          {selectedSalesOrder && selectedTab === 1 && (
            <FindingsTab
              selectedSalesOrder={selectedSalesOrder}
              type="Findings"
              canEditConsultation={canEditConsultation}
            />
          )}
          {selectedSalesOrder && selectedTab === 2 && (
            <FindingsTab
              selectedSalesOrder={selectedSalesOrder}
              type="Recommendations"
              canEditConsultation={canEditConsultation}
            />
          )}
          {selectedSalesOrder && selectedTab === 3 && (
            <DocumentsTab
              selectedSalesOrder={selectedSalesOrder}
              canEditConsultation={canEditConsultation}
            />
          )}
        </>
      ) : (
        <>
          <CustomPagination
            itemsPerPage={10}
            items={sortedDisplaySalesOrders}
            style={{ margin: '0px' }}
          >
            {(paginatedItems: any[]) => (
              <ReportTable
                columns={[
                  {
                    name: 'ID',
                    path: 'sales_order.sales_order_id',
                    type: 'ID',
                  },
                  {
                    name: translations('last_name'),
                    path: 'customer_person.person_lastname',
                    type: 'TEXT',
                  },
                  {
                    name: translations('first_name'),
                    path: 'customer_person.person_firstname',
                    type: 'TEXT',
                  },
                  {
                    name: translations('status'),
                    path: 'work_order.work_order_status_id',
                    type: 'STATUS',
                  },
                  {
                    name: translations('service'),
                    path: 'translated_service_name',
                    type: 'TEXT',
                  },
                  {
                    name: translations('booking.date'),
                    path: 'sales_order.datetime_create',
                    type: 'DATETIME',
                    sortOrder: bookingSortOrder,
                    onSort: () => handleDateSort('booking'),
                  },
                  {
                    name: translations('doctor.assigned'),
                    path: 'sales_order_appointment.person_assign_consutation_name',
                    type: 'TEXT',
                  },
                  {
                    name: translations('appointment.date'),
                    path: 'sales_order_appointment.start',
                    type: 'DATETIME',
                    sortOrder: appointmentSortOrder,
                    onSort: () => handleDateSort('appointment'),
                  },
                ]}
                rows={paginatedItems}
                isInReport={true}
                actions={[
                  {
                    displayName: translations('view'),
                    onClick: (row) => handleActionClick(row),
                  },
                ]}
              />
            )}
          </CustomPagination>
          {getSalesOrdersStatus === 'pending' && <Spinner />}
        </>
      )}
    </div>
  );
};

export default Consultations;
